import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeQuizFeedback,
  SelectedTab,
  selectIsQuizFeedbackOpen,
  selectQuizFeedbackParentQuizId,
  selectSelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  addInteractionToQuizFeedback,
  selectIsPlaying,
  selectSelectedElementId,
  selectTakes,
  setSelectedInteraction,
} from "../../app/slices/TrainingSlice";
import { findQuizById } from "../../app/slices/helpers/QuizFeedbackLocator";
import Interaction from "../../models/interactions/Interaction";
import { QuizFeedbackSlot } from "../../models/Quiz";
import { getFeedbackArray, isQuizFeedbackEnabled } from "../../models/QuizFeedbackHelper";
import { useTakeInteractionDndContext } from "../training_timeline/TakeInteractionDndProvider";

// explicit close (rail / close button): restore the parent quiz as the
// selection so the user lands back on the quiz, not a nested feedback item
export function useCloseQuizFeedback() {
  const dispatch = useDispatch();
  const parentQuizId = useSelector(selectQuizFeedbackParentQuizId);

  return () => {
    if (parentQuizId) dispatch(setSelectedInteraction(parentQuizId));
    dispatch(closeQuizFeedback());
  };
}

export default function useQuizFeedbackColumn() {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsQuizFeedbackOpen);
  const parentQuizId = useSelector(selectQuizFeedbackParentQuizId);
  const takes = useSelector(selectTakes);
  const selectedElementId = useSelector(selectSelectedElementId);
  const isPlaying = useSelector(selectIsPlaying);
  const selectedTab = useSelector(selectSelectedTab);
  const { isDragActive, isDragActiveRef } = useTakeInteractionDndContext();

  // derive the parent quiz independently of the current selection, so the
  // column stays open while a nested feedback interaction is selected
  const quizLocation = useMemo(
    () => findQuizById(takes, parentQuizId),
    [takes, parentQuizId]
  );
  const quiz = quizLocation?.quiz;
  const parentTakeId = quizLocation
    ? (takes[quizLocation.takeIndex]?.id ?? "")
    : "";

  useEffect(() => {
    if (!isOpen) return;
    if (quiz && !isQuizFeedbackEnabled(quiz)) {
      dispatch(setSelectedInteraction(quiz.id));
      dispatch(closeQuizFeedback());
      return;
    }

    const isSelectionInsideFeedback =
      selectedElementId === parentQuizId ||
      (quiz !== undefined &&
        [
          ...getFeedbackArray(quiz, "correct"),
          ...getFeedbackArray(quiz, "wrong"),
        ].some((interaction) => interaction.id === selectedElementId));

    // While a drag is in flight the selection follows the dragged row; the
    // panel must stay open so its slots remain valid drop targets. The ref
    // covers the drag-start render, which can commit before the provider's
    // isDragActive state does; the state keeps this effect re-running when
    // the drag ends so an abandoned drag still closes the panel.
    if (
      !quiz ||
      isPlaying ||
      selectedTab !== SelectedTab.Timeline ||
      (!isSelectionInsideFeedback && !isDragActive && !isDragActiveRef.current)
    ) {
      dispatch(closeQuizFeedback());
    }
  }, [
    isOpen,
    quiz,
    isPlaying,
    selectedTab,
    selectedElementId,
    parentQuizId,
    isDragActive,
    isDragActiveRef,
    dispatch,
  ]);

  const close = useCloseQuizFeedback();

  const addFeedbackInteraction = (
    slot: QuizFeedbackSlot,
    interaction: Interaction
  ) => {
    if (!isQuizFeedbackEnabled(quiz)) return;
    dispatch(
      addInteractionToQuizFeedback({ quizId: parentQuizId, slot, interaction })
    );
  };

  return { isOpen, quiz, parentTakeId, close, addFeedbackInteraction };
}
